import { RoutingSettings_Read, RoutingSettings_Update } from '@treadinc/horizon-api-spec';

import { RoutingSettingsDto } from '~pages/Settings/RouteOptimization/routingSettingsSchema';

import { BasicDepartment } from '../useDepartment/models';

// The four hours-of-service limits are stored in whole minutes and shown in hours.
// Conversion happens here and nowhere else, so parse and deparseUpdate stay inverses.
const MINUTES_PER_HOUR = 60;

const parseOptionalBound = (value: string) =>
  value.trim() === '' ? null : Number(value);

export class RoutingSettings {
  constructor(
    private _id: string,
    private _company_id: string,
    private _department_id: string | null,
    private _department: BasicDepartment | null,
    private _pickup_onsite_minutes: number,
    private _dropoff_onsite_minutes: number,
    private _interstate_drive_hours: number,
    private _interstate_total_hours: number,
    private _intrastate_drive_hours: number,
    private _intrastate_total_hours: number,
    private _min_loads_per_truck: number | null,
    private _max_loads_per_truck: number | null,
    private _max_orders_per_truck: number | null,
    private _require_all_trucks: boolean,
    private _resolve_allow_retruck_accepted: boolean,
    private _resolve_unaccepted_moveable: boolean,
  ) {}

  // Two decimals keeps a non-round stored value legible in a number input (650 minutes
  // would otherwise render as 10.833333333333334) and still round-trips to the same
  // minute through deparseUpdate.
  private static minutesToHours(minutes: number) {
    return Math.round((minutes / MINUTES_PER_HOUR) * 100) / 100;
  }

  public static parse(proto: RoutingSettings_Read) {
    return new RoutingSettings(
      proto.id,
      proto.company_id,
      proto.department_id ?? null,
      proto.department ? BasicDepartment.parse(proto.department) : null,
      proto.pickup_onsite_minutes,
      proto.dropoff_onsite_minutes,
      RoutingSettings.minutesToHours(proto.interstate_drive_minutes),
      RoutingSettings.minutesToHours(proto.interstate_total_minutes),
      RoutingSettings.minutesToHours(proto.intrastate_drive_minutes),
      RoutingSettings.minutesToHours(proto.intrastate_total_minutes),
      proto.min_loads_per_truck ?? null,
      proto.max_loads_per_truck ?? null,
      proto.max_orders_per_truck ?? null,
      proto.require_all_trucks,
      proto.resolve_allow_retruck_accepted,
      proto.resolve_unaccepted_moveable,
    );
  }

  public static deparseUpdate(dto: RoutingSettingsDto): RoutingSettings_Update {
    return {
      department_id: dto.departmentId ?? null,
      pickup_onsite_minutes: Number(dto.pickupOnsiteMinutes),
      dropoff_onsite_minutes: Number(dto.dropoffOnsiteMinutes),
      interstate_drive_minutes: Math.round(
        Number(dto.interstateDriveHours) * MINUTES_PER_HOUR,
      ),
      interstate_total_minutes: Math.round(
        Number(dto.interstateTotalHours) * MINUTES_PER_HOUR,
      ),
      intrastate_drive_minutes: Math.round(
        Number(dto.intrastateDriveHours) * MINUTES_PER_HOUR,
      ),
      intrastate_total_minutes: Math.round(
        Number(dto.intrastateTotalHours) * MINUTES_PER_HOUR,
      ),
      min_loads_per_truck: parseOptionalBound(dto.minLoadsPerTruck),
      max_loads_per_truck: parseOptionalBound(dto.maxLoadsPerTruck),
      max_orders_per_truck: parseOptionalBound(dto.maxOrdersPerTruck),
      require_all_trucks: dto.requireAllTrucks,
      resolve_allow_retruck_accepted: dto.resolveAllowRetruckAccepted,
      resolve_unaccepted_moveable: dto.resolveUnacceptedMoveable,
    };
  }

  public get id() {
    return this._id;
  }

  public get companyId() {
    return this._company_id;
  }

  public get departmentId() {
    return this._department_id;
  }

  public get department() {
    return this._department;
  }

  public get pickupOnsiteMinutes() {
    return this._pickup_onsite_minutes;
  }

  public get dropoffOnsiteMinutes() {
    return this._dropoff_onsite_minutes;
  }

  public get interstateDriveHours() {
    return this._interstate_drive_hours;
  }

  public get interstateTotalHours() {
    return this._interstate_total_hours;
  }

  public get intrastateDriveHours() {
    return this._intrastate_drive_hours;
  }

  public get intrastateTotalHours() {
    return this._intrastate_total_hours;
  }

  public get minLoadsPerTruck() {
    return this._min_loads_per_truck;
  }

  public get maxLoadsPerTruck() {
    return this._max_loads_per_truck;
  }

  public get maxOrdersPerTruck() {
    return this._max_orders_per_truck;
  }

  public get requireAllTrucks() {
    return this._require_all_trucks;
  }

  public get resolveAllowRetruckAccepted() {
    return this._resolve_allow_retruck_accepted;
  }

  public get resolveUnacceptedMoveable() {
    return this._resolve_unaccepted_moveable;
  }
}
